import type {
  DomainGuid,
  EvaluatedStatus
} from '@/features/execute-calculation-set/overmind/calculation';
import type {
  FeaturesInLayer,
  FeatureWithIDs,
} from '@/overmind/effects/adapters/baseadapter';
import type {
  HighlightStates,
} from '../utils/styleMapElement';
import { OrderedLayerImportance } from './OrderLayer';

function toFeaturesInLayers(featuresInLayers: { features: FeatureWithIDs<unknown>[] }[]) {
  const myOwnFeaturesInLayers = featuresInLayers.map(({ features }, index) => ({
    features,
    type: 'FeatureCollection',
    layerId: index
  }));
  const layersWithImportance = myOwnFeaturesInLayers.map(layer => ({
    layer,
    importance: new OrderedLayerImportance()
  }));
  return Array.from(layersWithImportance)
    .reverse() // The top layer should be drawn last
}

export function createMyOwnFeaturesInLayers(
  featuresInLayers: FeaturesInLayer[],
  statuses: HighlightStates,
  startingElementId: DomainGuid | undefined) {
  const allFeaturesInAllLayers = featuresInLayers.flatMap(
    featureLayer => featureLayer.features
  );

  const highlightedAsBadStatusFeatures = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'Bad'
  );
  const highlightedAsWarningStatusFeatures = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'Warning'
  );
  const startingElementsFeatures = allFeaturesInAllLayers.filter(
    f => f.gc.globalId === startingElementId
  );
  const highlightedAsGoodStatusFeatures = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'Good'
  );

  // Not evaluated objects
  const notEvaluatedFeatures = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'NotEvaluated'
  );

  // Remove starting element from NotEvaluated features, because this is in different list already
  const irrelevantFeatures = notEvaluatedFeatures.filter(
    feature => feature.id !== startingElementId
  );
  const notHighlightedStatusFeatures = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'NotHighlighted'
  );

  const myOwnFeaturesInLayers = [
    { features: highlightedAsBadStatusFeatures },
    { features: highlightedAsWarningStatusFeatures },
    { features: startingElementsFeatures },
    { features: highlightedAsGoodStatusFeatures },
    { features: irrelevantFeatures },
    { features: notHighlightedStatusFeatures }
  ];

  return toFeaturesInLayers(myOwnFeaturesInLayers);
}

export function getStatus(
  globalId: DomainGuid,
  statuses: HighlightStates
): EvaluatedStatus | 'NotHighlighted' {
  if (statuses.type === 'NONE_HIGHLIGHTED') {
    return 'NotHighlighted';
  } else {
    const status = statuses.context.highlighted[globalId];
    if (status) return status;

    return 'NotHighlighted';
  }
}

export function createMyOwnFeaturesInLayersV2(
  featuresInLayers: FeaturesInLayer[],
  statuses: HighlightStates,
  startingElementId: DomainGuid | undefined) {
  const allFeaturesInAllLayers = featuresInLayers.flatMap(
    featureLayer => featureLayer.features
  );

  const badPoints = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'Bad' && f.geometry.type === 'Point'
  );

  const badLines = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'Bad' && f.geometry.type === 'LineString'
  );

  const badPolygons = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'Bad' && f.geometry.type === 'Polygon'
  );

  const warningPoints = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'Warning' && f.geometry.type === 'Point'
  );

  const warningLines = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'Warning' && f.geometry.type === 'LineString'
  );

  const warningPolygons = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'Warning' && f.geometry.type === 'Polygon'
  );

  const startingElementPoints = allFeaturesInAllLayers.filter(
    f => f.gc.globalId === startingElementId && f.geometry.type === 'Point'
  );

  const startingElementLines = allFeaturesInAllLayers.filter(
    f => f.gc.globalId === startingElementId && f.geometry.type === 'LineString'
  );

  const startingElementPolygons = allFeaturesInAllLayers.filter(
    f => f.gc.globalId === startingElementId && f.geometry.type === 'Polygon'
  );

  const goodPoints = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'Good' && f.geometry.type === 'Point'
  );

  const goodLines = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'Good' && f.geometry.type === 'LineString'
  );

  const goodPolygons = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'Good' && f.geometry.type === 'Polygon'
  );

  // Not evaluated objects
  const notEvaluatedFeatures = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'NotEvaluated'
  );

  // Remove starting element from NotEvaluated features, because this is in different list already
  const irrelevantFeatures = notEvaluatedFeatures.filter(
    feature => feature.id !== startingElementId
  );
  const notHighlightedStatusFeatures = allFeaturesInAllLayers.filter(
    f => getStatus(f.gc.globalId, statuses) === 'NotHighlighted'
  );

  const myOwnFeaturesInLayers = [
    { features: badPoints },
    { features: badLines },
    { features: badPolygons },
    { features: warningPoints },
    { features: warningLines },
    { features: warningPolygons },
    { features: startingElementPoints },
    { features: startingElementLines },
    { features: startingElementPolygons },
    { features: goodPoints },
    { features: goodLines },
    { features: goodPolygons },
    { features: irrelevantFeatures },
    { features: notHighlightedStatusFeatures }]

  return toFeaturesInLayers(myOwnFeaturesInLayers);
}
